import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Trash2, Printer, Boxes, FileText, Lock, Pencil, Check, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  pending: "bg-zinc-100 text-zinc-700 border-zinc-300",
  in_production: "bg-orange-50 text-orange-700 border-orange-300",
  ready: "bg-orange-100 text-orange-800 border-orange-400",
  dispatched: "bg-orange-600 text-white border-orange-700",
  delivered: "bg-zinc-900 text-white border-zinc-900",
  closed: "bg-emerald-700 text-white border-emerald-800",
  cancelled: "bg-white text-zinc-500 border-zinc-400 line-through",
};

const NEXT_STATUS = ["pending", "in_production", "ready", "dispatched", "delivered", "cancelled"];

export default function Orders() {
  const { user } = useAuth();
  const role = user?.role;
  // Permission matrix per role
  const canCreate = ["admin", "sales"].includes(role);
  const canEdit = role === "admin";          // edits require admin (Option B: button greyed for others)
  const canDelete = role === "admin";        // deletes require admin
  const canAssign = ["admin", "production"].includes(role);
  const canPrintProdNote = ["admin", "production"].includes(role);
  const canPrintClientNote = ["admin", "sales"].includes(role);
  const canPrintDC = ["admin", "sales", "dispatch"].includes(role); // production CANNOT see DC
  const canViewCostSheet = ["admin", "sales"].includes(role);
  const canChangeStatus = ["admin", "sales", "production", "dispatch"].includes(role);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [assignedMap, setAssignedMap] = useState({}); // {orderId: assignedCartonsCount}
  const [showForm, setShowForm] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [filter, setFilter] = useState("");
  // Show closed orders (dispatched / delivered / closed / cancelled) inside a
  // collapsed accordion by default — massively reduces the amount of screen
  // real estate the Orders page eats up over time.
  const [showClosed, setShowClosed] = useState(false);
  const [closedSearch, setClosedSearch] = useState("");

  const CLOSED_STATUSES = ["dispatched", "delivered", "closed", "cancelled"];
  const isClosed = (o) => CLOSED_STATUSES.includes(o?.status);

  const load = async () => {
    const params = filter ? { status: filter } : {};
    const [o, c, p, a] = await Promise.all([
      api.get("/orders", { params }),
      api.get("/customers"),
      api.get("/products"),
      api.get("/orders/assignments-summary"),
    ]);
    setOrders(o.data); setCustomers(c.data); setProducts(p.data); setAssignedMap(a.data || {});
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = filter ? { status: filter } : {};
        const [o, c, p, a] = await Promise.all([
          api.get("/orders", { params }),
          api.get("/customers"),
          api.get("/products"),
          api.get("/orders/assignments-summary"),
        ]);
        if (cancelled) return;
        setOrders(o.data); setCustomers(c.data); setProducts(p.data); setAssignedMap(a.data || {});
      } catch { /* no-op */ }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const closeOrder = async (o) => {
    if (!window.confirm(`Close order ${o.order_no} early?\n\nWhatever cartons have already been dispatched will be kept as the final fulfilled quantity. Any remaining unmet quantity will be voided and reserved cartons released back to inventory.\n\nThis cannot be undone.`)) return;
    try {
      const r = await api.post(`/orders/${o.id}/close`);
      toast.success(`Closed ${o.order_no}`);
      load();
      // Show a per-line summary if anything was voided.
      const voided = (r.data.close_summary || []).filter((s) => s.voided > 0);
      if (voided.length > 0) {
        const lines = voided.map((s) => `${s.sku}: ordered ${s.ordered}, delivered ${s.delivered}, voided ${s.voided}`).join("\n");
        toast.message("Partial fulfilment recorded", { description: lines });
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const deleteOrder = async (o) => {
    const msg = ["dispatched", "delivered"].includes(o.status)
      ? `Delete ${o.order_no}? This order is already ${o.status} — dispatched cartons will remain marked as dispatched (historical record kept). The order record itself will be removed permanently.`
      : `Delete order ${o.order_no}? This will release any cartons reserved for it.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success(`Deleted ${o.order_no}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Sales Pipeline</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Order Management</h1>
        </div>
        {canCreate && (
          <button data-testid="new-order-btn" onClick={() => setShowForm(true)} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2 active:scale-[0.99]">
            <Plus size={16} /> New Order
          </button>
        )}
      </div>

      <div className="bg-white border border-zinc-300 p-3 flex gap-2 items-center flex-wrap" data-testid="orders-view-toggle">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">View</span>
        {[
          { k: "open",   label: "Open",   count: orders.filter((o) => !isClosed(o)).length, testid: "filter-open" },
          { k: "closed", label: "Closed", count: orders.filter((o) => isClosed(o)).length,  testid: "filter-closed" },
          { k: "all",    label: "All",    count: orders.length,                              testid: "filter-all" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={t.testid}
            onClick={() => setFilter(t.k === "open" ? "" : t.k === "closed" ? "__closed__" : "__all__")}
            className={`text-[11px] uppercase font-mono px-3 py-1 border ${
              (t.k === "open"   && filter === "") ||
              (t.k === "closed" && filter === "__closed__") ||
              (t.k === "all"    && filter === "__all__")
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"
            }`}
          >
            {t.label} <span className="opacity-70 ml-1">({t.count})</span>
          </button>
        ))}
        <span className="text-[9px] font-mono text-zinc-400 ml-2 hidden sm:inline">
          Open = pending · in production · ready. Closed = dispatched · delivered · closed · cancelled.
        </span>
      </div>

      <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="orders-table">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Order #</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Customer</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Order Date</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Delivery</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Items</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Status</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Action</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Print</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Manage</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {orders.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-zinc-500 py-12 text-sm font-sans">No orders found. Create your first sales order.</td></tr>
            ) : orders.map((o) => {
              const assignedCount = assignedMap[o.id] || 0;
              const isDispatched = ["dispatched", "delivered"].includes(o.status);
              // DC unlocked if cartons are assigned OR order has been dispatched/delivered (legacy)
              const dcUnlocked = assignedCount !== 0 || isDispatched;
              const isLocked = ["dispatched", "delivered"].includes(o.status);
              return (
                <tr key={o.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-semibold text-zinc-900">{o.order_no}</td>
                  <td className="px-3 py-2 font-sans">{customerName(o.customer_id)}</td>
                  <td className="px-3 py-2">{o.order_date}</td>
                  <td className="px-3 py-2 text-orange-700">{o.delivery_date}</td>
                  <td className="px-3 py-2 text-right">{o.items.length}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase border px-2 py-0.5 ${STATUS_COLORS[o.status]}`}>{o.status.replace("_", " ")}</span></td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`status-${o.id}`}
                      value={o.status}
                      disabled={!canChangeStatus}
                      onChange={(e) => setStatus(o.id, e.target.value)}
                      className="text-[11px] font-mono uppercase border border-zinc-300 px-1 py-0.5 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {NEXT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {canAssign && (
                      <Link
                        data-testid={`assign-stock-${o.id}`}
                        to={`/orders/${o.id}/assign`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                      >
                        <Boxes size={12} /> Assign
                      </Link>
                    )}
                    {canPrintClientNote && (
                      <Link
                        data-testid={`print-client-note-${o.id}`}
                        to={`/orders/${o.id}/print?type=client_note`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                        title="Client-facing acknowledgment (always available)"
                      >
                        <FileText size={12} /> Client Note
                      </Link>
                    )}
                    {canPrintProdNote && (
                      <Link
                        data-testid={`print-prod-note-${o.id}`}
                        to={`/orders/${o.id}/print?type=production_note`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                        title="Internal production note (raw material auto-adjusts with assigned stock)"
                      >
                        <FileText size={12} /> Prod Note
                      </Link>
                    )}
                    {canPrintDC && (dcUnlocked ? (
                      <Link
                        data-testid={`print-dispatch-${o.id}`}
                        to={`/orders/${o.id}/print?type=dispatch`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-orange-300 bg-orange-50 text-orange-800 px-2 py-1 hover:bg-orange-100"
                      >
                        <Printer size={12} /> Delivery Challan
                      </Link>
                    ) : (
                      <span
                        data-testid={`print-dispatch-${o.id}-locked`}
                        title="Assign stock to enable Delivery Challan"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-dashed border-zinc-300 text-zinc-400 px-2 py-1 cursor-not-allowed select-none"
                      >
                        <Lock size={11} /> Delivery Challan
                      </span>
                    ))}
                    {canViewCostSheet && (
                      <Link
                        data-testid={`cost-sheet-${o.id}`}
                        to={`/orders/${o.id}/cost-sheet`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 ml-1"
                        title="Actual FIFO raw material cost for this order"
                      >
                        <IndianRupee size={12} /> Cost Sheet
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {isLocked ? (
                      <span
                        data-testid={`edit-order-${o.id}-locked`}
                        title="Cannot edit a dispatched / delivered order"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-dashed border-zinc-300 text-zinc-400 px-2 py-1 mr-1 cursor-not-allowed select-none"
                      >
                        <Lock size={11} /> Edit
                      </span>
                    ) : canEdit ? (
                      <button
                        data-testid={`edit-order-${o.id}`}
                        onClick={() => setEditOrder(o)}
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                        title="Edit this order"
                      >
                        <Pencil size={11} /> Edit
                      </button>
                    ) : (
                      <span
                        data-testid={`edit-order-${o.id}-admin-only`}
                        title="Editing requires admin approval"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-dashed border-zinc-300 text-zinc-400 px-2 py-1 mr-1 cursor-not-allowed select-none"
                      >
                        <Lock size={11} /> Edit
                      </span>
                    )}
                    {canDelete ? (
                      <button
                        data-testid={`delete-order-${o.id}`}
                        onClick={() => deleteOrder(o)}
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100"
                        title={isLocked ? "Delete dispatched order (keeps carton history)" : "Delete this order (releases any assigned cartons)"}
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    ) : (
                      <span
                        data-testid={`delete-order-${o.id}-admin-only`}
                        title="Deletion requires admin approval"
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-dashed border-zinc-300 text-zinc-400 px-2 py-1 cursor-not-allowed select-none"
                      >
                        <Lock size={11} /> Delete
                      </span>
                    )}
                    {canEdit && !["closed", "cancelled"].includes(o.status) && (
                      <button
                        data-testid={`close-order-${o.id}`}
                        onClick={() => closeOrder(o)}
                        className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-emerald-600 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 ml-1"
                        title="Close this order early — keeps already-dispatched cartons, voids the remainder"
                      >
                        <Check size={11} /> Close
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(showForm || editOrder) && (
        <OrderForm
          customers={customers}
          products={products}
          initial={editOrder}
          onClose={() => { setShowForm(false); setEditOrder(null); }}
          onSaved={() => { setShowForm(false); setEditOrder(null); load(); }}
        />
      )}
    </div>
  );
}

function OrderForm({ customers, products, initial, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = !!initial;
  const [customer_id, setCustomer] = useState(initial?.customer_id || customers[0]?.id || "");
  const [order_date, setOrderDate] = useState(initial?.order_date || today);
  const [delivery_date, setDeliveryDate] = useState(initial?.delivery_date || today);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [items, setItems] = useState(
    initial?.items?.length
      ? initial.items.map((it) => ({
          product_id: it.product_id || "",
          quantity_rolls: it.quantity_rolls ?? "",
          weight_category: it.weight_category || "pure_net",
          custom_tare_kg: it.custom_tare_kg ?? 0,
        }))
      : [{ product_id: "", quantity_rolls: "", weight_category: "pure_net", custom_tare_kg: 0 }],
  );
  const [busy, setBusy] = useState(false);

  const updateItem = (idx, patch) => {
    const c = [...items];
    c[idx] = { ...c[idx], ...patch };
    setItems(c);
  };
  const addItem = () => setItems([...items, { product_id: "", quantity_rolls: "", weight_category: "pure_net", custom_tare_kg: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const totalCartons = items.reduce((s, it) => s + (parseInt(it.quantity_rolls) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!customer_id) return toast.error("Add a customer first");
    const cleanItems = items.filter((it) => it.product_id && it.quantity_rolls > 0);
    if (cleanItems.length === 0) return toast.error("Add at least one SKU + quantity");
    setBusy(true);
    try {
      // Enrich each item with its category spec for downstream use (printable doc, dispatch)
      const enriched = cleanItems.map((it) => {
        const p = products.find((x) => x.id === it.product_id);
        return {
          product_id: it.product_id,
          quantity_rolls: parseInt(it.quantity_rolls) || 0,
          weight_category: it.weight_category || "pure_net",
          custom_tare_kg: it.weight_category === "custom" ? (parseFloat(it.custom_tare_kg) || 0) : 0,
          spec: p?.spec || {},
        };
      });
      const payload = { customer_id, order_date, delivery_date, notes, items: enriched };
      if (isEdit) {
        await api.put(`/orders/${initial.id}`, payload);
        toast.success(`Updated ${initial.order_no}`);
      } else {
        await api.post("/orders", payload);
        toast.success("Order created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10" data-testid="order-form-modal">
      <div className="bg-white border border-zinc-300 w-full max-w-3xl">
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{isEdit ? "Edit Sales Order" : "New Sales Order"}</div>
            <h2 className="text-xl font-semibold">{isEdit ? `Edit ${initial.order_no}` : "Create Order"}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="close-order-form"><X /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-zinc-900 uppercase mb-1 block">Customer *</label>
              <select data-testid="order-customer" value={customer_id} onChange={(e) => setCustomer(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono">
                <option value="">-- select --</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.company && `(${c.company})`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-zinc-900 uppercase mb-1 block">Order Date</label>
              <input data-testid="order-date" type="date" value={order_date} onChange={(e) => setOrderDate(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-zinc-900 uppercase mb-1 block">Delivery Date</label>
              <input data-testid="order-delivery" type="date" value={delivery_date} onChange={(e) => setDeliveryDate(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono" />
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2 border-b border-zinc-200 pb-1">Line Items — SKU & Quantity</div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-1 text-xs font-mono text-zinc-500 pb-2">#{idx + 1}</div>
                  <div className="col-span-5">
                    <label className="text-[10px] font-semibold uppercase mb-1 block">Category (SKU)</label>
                    <select
                      data-testid={`item-sku-${idx}`}
                      value={it.product_id}
                      onChange={(e) => updateItem(idx, { product_id: e.target.value })}
                      className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono"
                    >
                      <option value="">-- select category --</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.sku}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="text-[10px] font-semibold uppercase mb-1 block">Weight Basis *</label>
                    <select
                      data-testid={`item-weight-cat-${idx}`}
                      value={it.weight_category}
                      onChange={(e) => updateItem(idx, { weight_category: e.target.value, custom_tare_kg: e.target.value === "custom" ? (it.custom_tare_kg || 0) : 0 })}
                      className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono"
                    >
                      <option value="gross">Gross Weight</option>
                      <option value="net">Net Weight</option>
                      <option value="pure_net">100% Net Weight</option>
                      <option value="custom">Custom Tare</option>
                    </select>
                    {it.weight_category === "custom" && (
                      <input
                        data-testid={`item-custom-tare-${idx}`}
                        type="number"
                        step="0.001"
                        min="0"
                        value={it.custom_tare_kg ?? 0}
                        onChange={(e) => updateItem(idx, { custom_tare_kg: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                        placeholder="Tare kg/carton"
                        className="mt-1 w-full h-8 px-2 text-xs border border-orange-300 bg-orange-50 font-mono"
                        title="Manual tare weight in kg, subtracted from each carton's gross weight"
                      />
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-semibold uppercase mb-1 block">Cartons</label>
                    <input
                      data-testid={`item-qty-${idx}`}
                      type="number"
                      min="1"
                      value={it.quantity_rolls}
                      placeholder="0"
                      onChange={(e) => updateItem(idx, { quantity_rolls: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono text-right"
                    />
                  </div>
                  <div className="col-span-1 text-right">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="h-9 px-2 text-zinc-500 hover:text-zinc-900"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addItem} data-testid="add-item-btn" className="mt-3 text-xs uppercase font-mono px-3 py-1.5 border border-dashed border-zinc-400 bg-white hover:bg-zinc-100 inline-flex items-center gap-1">
              <Plus size={12} /> Add Line Item
            </button>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-zinc-900 uppercase mb-1 block">Remarks / Notes</label>
            <textarea data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Special instructions, delivery address, transport details…" className="w-full p-2 text-sm border border-zinc-300 bg-white font-mono" />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-zinc-200">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total Cartons</div>
              <div className="font-mono text-2xl font-semibold text-orange-700">{totalCartons}</div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm uppercase border border-zinc-300 bg-white hover:bg-zinc-100">Cancel</button>
              <button data-testid="save-order-btn" type="submit" disabled={busy} className="px-5 py-2 text-sm uppercase bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">{busy ? "Saving..." : (isEdit ? "Save Changes" : "Create Order")}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
